/**
 * Alerts: bills falling overdue, and figures going stale.
 *
 * HONEST ABOUT WHAT THIS IS. There is no push server — a zero-knowledge design
 * has nothing on the server that could decide a notification is due, because
 * the server cannot read the books. So these fire when the app is OPEN. That is
 * a genuine limitation, it is stated in the feature's own description, and the
 * alternative (a server that knows your ageing) would trade away the entire
 * security argument for a convenience.
 *
 * WHAT MAKES AN ALERT USEFUL IS WHAT IT DOES NOT SAY. Notifying about the same
 * overdue bill on every app open trains the user to dismiss everything, so each
 * alert has a key and a key fires at most once a day.
 */
import { getSetting, setSetting } from "./sync/db.js";

const SEEN_KEY = "alerts:seen";
const THRESHOLDS_KEY = "alerts:thresholds";

/** Once per day per subject. More than that is nagging, less is missing news. */
const QUIET_HOURS = 20;

export type Alert = {
  /** stable across app opens — this is what stops a repeat */
  key: string;
  title: string;
  body: string;
};

export type AlertInput = {
  companyId: string;
  /** bills already past their due date, with money still on them */
  overdue: Array<{ ledger: string; amount: number; daysOverdue: number }>;
  /** hours since the snapshot was generated */
  staleHours: number | null;
  /** total receivable, for the ceiling rule */
  totalReceivable?: number;
  /** the single largest party balance, for the concentration rule */
  largestParty?: { ledger: string; amount: number };
  /** customers who have gone quiet, for the dormancy rule */
  dormantCount?: number;
  /** thresholds the user set; a rule with no threshold does not fire */
  thresholds?: Thresholds;
};

/**
 * Limits the user sets, in Settings.
 *
 * Every one defaults to OFF rather than to a guessed number. A threshold
 * invented by the software fires on somebody's ordinary Tuesday and teaches
 * them to swipe alerts away — after which the one that mattered goes with the
 * rest. A limit is only meaningful if the person running the business chose it.
 */
export type Thresholds = {
  /** warn when total receivable exceeds this */
  receivableCeiling?: number;
  /** warn when any one party owes more than this */
  partyCeiling?: number;
  /** warn when this many customers have gone quiet */
  dormantCeiling?: number;
};

/** Matches the app's own staleness banner — one definition of "too old". */
export const STALE_AFTER_HOURS = 6;

export function buildAlerts(input: AlertInput, today: string): Alert[] {
  const alerts: Alert[] = [];

  if (input.overdue.length > 0) {
    const total = input.overdue.reduce((sum, bill) => sum + bill.amount, 0);
    const worst = input.overdue.reduce((a, b) => (a.daysOverdue >= b.daysOverdue ? a : b));

    alerts.push({
      // Keyed by DAY, not by content: a new bill crossing its due date should
      // not re-alert about the eleven that were already overdue yesterday.
      key: `overdue:${input.companyId}:${today}`,
      title: `${input.overdue.length} overdue ${input.overdue.length === 1 ? "bill" : "bills"}`,
      body:
        `${formatShort(total)} outstanding past due. ` +
        `Oldest: ${worst.ledger}, ${worst.daysOverdue} days.`,
    });
  }

  const limits = input.thresholds ?? {};

  /*
   * Receivables over a ceiling.
   *
   * Keyed by the DAY and by which side of the line it fell, not by the amount:
   * a total that wobbles either side of the limit must not alert on every
   * crossing, and an amount in the key would do exactly that.
   */
  if (
    limits.receivableCeiling !== undefined &&
    input.totalReceivable !== undefined &&
    input.totalReceivable > limits.receivableCeiling
  ) {
    alerts.push({
      key: `receivable-ceiling:${input.companyId}:${today}`,
      title: "Receivables above your limit",
      body:
        `${formatShort(input.totalReceivable)} is outstanding, above the ` +
        `${formatShort(limits.receivableCeiling)} you set.`,
    });
  }

  if (
    limits.partyCeiling !== undefined &&
    input.largestParty !== undefined &&
    input.largestParty.amount > limits.partyCeiling
  ) {
    alerts.push({
      key: `party-ceiling:${input.companyId}:${input.largestParty.ledger}:${today}`,
      title: `${input.largestParty.ledger} is over your limit`,
      body:
        `They owe ${formatShort(input.largestParty.amount)}, above the ` +
        `${formatShort(limits.partyCeiling)} you set for a single customer.`,
    });
  }

  if (
    limits.dormantCeiling !== undefined &&
    input.dormantCount !== undefined &&
    input.dormantCount > limits.dormantCeiling
  ) {
    alerts.push({
      key: `dormant-ceiling:${input.companyId}:${today}`,
      title: `${input.dormantCount} customers have gone quiet`,
      body: `Above the ${limits.dormantCeiling} you set. Worth a look at who they are.`,
    });
  }

  if (input.staleHours !== null && input.staleHours >= STALE_AFTER_HOURS) {
    alerts.push({
      key: `stale:${input.companyId}:${today}`,
      title: "Figures are out of date",
      body:
        `Last synced ${Math.floor(input.staleHours)} hours ago. ` +
        `The laptop may be asleep or TallyPrime closed.`,
    });
  }

  return alerts;
}

function formatShort(amount: number): string {
  const value = Math.abs(amount);
  if (value >= 10_000_000) return `₹${(value / 10_000_000).toFixed(2)} cr`;
  if (value >= 100_000) return `₹${(value / 100_000).toFixed(2)} lakh`;
  return `₹${Math.round(value).toLocaleString("en-IN")}`;
}

export async function loadThresholds(): Promise<Thresholds> {
  return getSetting<Thresholds>(THRESHOLDS_KEY, {});
}

export async function saveThresholds(next: Thresholds): Promise<void> {
  // An empty or non-numeric field means "no limit", and must delete the key
  // rather than store 0 — a ceiling of zero would fire on every sync forever.
  const clean: Thresholds = {};
  for (const key of ["receivableCeiling", "partyCeiling", "dormantCeiling"] as const) {
    const value = next[key];
    if (typeof value === "number" && Number.isFinite(value) && value > 0) clean[key] = value;
  }
  await setSetting(THRESHOLDS_KEY, clean);
}

/* ─────────────────────────────────────────────────────────────  delivery ── */

export type Permission = "granted" | "denied" | "default" | "unsupported";

export function permission(): Permission {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

/**
 * Ask for permission.
 *
 * Called ONLY from the Settings toggle, never on app start: a notification
 * prompt on first open, before the user has seen anything, is the fastest way
 * to a permanent "block".
 */
export async function requestPermission(): Promise<Permission> {
  if (typeof Notification === "undefined") return "unsupported";
  try {
    return await Notification.requestPermission();
  } catch {
    return "denied";
  }
}

/** Fire whatever has not been shown recently. Returns what was actually shown. */
export async function deliver(alerts: Alert[], now: Date = new Date()): Promise<Alert[]> {
  if (permission() !== "granted" || alerts.length === 0) return [];

  const seen = await getSetting<Record<string, string>>(SEEN_KEY, {});
  const shown: Alert[] = [];
  const next = { ...seen };

  for (const alert of alerts) {
    const last = seen[alert.key];
    if (last && hoursBetween(last, now) < QUIET_HOURS) continue;

    try {
      new Notification(alert.title, { body: alert.body, tag: alert.key });
      next[alert.key] = now.toISOString();
      shown.push(alert);
    } catch {
      // Some browsers only allow notifications from a service worker context.
      // Failing silently is right: an alert is a convenience, and throwing here
      // would break whatever screen the user was actually looking at.
    }
  }

  // Old keys are dated and would accumulate one entry per day forever.
  await setSetting(SEEN_KEY, prune(next, now));
  return shown;
}

function hoursBetween(iso: string, now: Date): number {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return Number.POSITIVE_INFINITY;
  return (now.getTime() - then) / 3_600_000;
}

function prune(seen: Record<string, string>, now: Date): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, at] of Object.entries(seen)) {
    if (hoursBetween(at, now) <= 24 * 7) out[key] = at;
  }
  return out;
}
